import React, { useState, useEffect, useRef, useCallback } from 'react';
import postcss from 'postcss'; // CodeAN-999 CSSParser replaced by postcss.parse - tested but functionality should be observed
import { find as _find } from 'lodash';

import RichEditorContent from 'components/RichEditorContent';

let counter = 0;

export const useAbstractCmsElement = (props) => {
  const { params, index, editMode, layout, effectsEnabled, showElementSettings } = props;

  // Initialize the ID only once when mounting.
  const idRef = useRef(null);
  if (idRef.current === null) {
    idRef.current = 'ms' + ++counter;
  }
  const id = idRef.current;

  // States
  const [shown, setShown] = useState(false);
  const [elementStyle, setElementStyle] = useState(null);

  // References for Lifecycle and Timer
  const allowStateChange = useRef(true);
  const animationResetTimeout = useRef(null);
  const prevProps = useRef(props);
  const isMounted = useRef(false);

  // === Helper functions (Getters) ===
  const getMainId = useCallback(() => {
    return id + '_' + index.replace(/\./g, '_');
  }, [id, index]);

  const getParam = useCallback(
    (name) => {
      return _find(params, ['name', name]);
    },
    [params]
  );

  const getParamValue = useCallback(
    (name, defaultValue = null) => {
      const param = getParam(name);
      if (param && param.value !== null && param.value !== '' && param.value !== undefined) {
        return param.value;
      }
      return defaultValue;
    },
    [getParam]
  );

  const getCustomId = useCallback(() => {
    const customId = getParamValue('elementId');
    return customId ? { id: customId } : null;
  }, [getParamValue]);

  // Function to trigger the "shown" state
  const triggerShown = useCallback(() => {
    if (allowStateChange.current) {
      setShown(true);
    }
  }, []);

  const triggerShowSettings = useCallback(
    (e) => {
      if (showElementSettings) {
        showElementSettings(e, index);
      }
    },
    [showElementSettings, index]
  );

  // === Styling & Classes ===
  const getResponsiveClasses = useCallback(() => {
    let classes = ' ';

    if (getParamValue('hiddenPhone', 'no') === 'yes') {
      classes += 'hidden-xs ';
    }
    if (getParamValue('hiddenTablet', 'no') === 'yes') {
      classes += 'hidden-sm hidden-md ';
    }
    if (getParamValue('hiddenDesktop', 'no') === 'yes') {
      classes += 'hidden-lg ';
    }

    return editMode ? '' : classes;
  }, [getParamValue, editMode]);

  const getPaddingClasses = useCallback(
    (defaultValue = 0) => {
      let classes = ' ';

      classes += 'p-t-' + getParamValue('paddingTop', defaultValue) + ' ';
      classes += 'p-b-' + getParamValue('paddingBottom', defaultValue) + ' ';

      classes += 'sm-p-t-' + getParamValue('smPaddingTop', getParamValue('paddingTop', defaultValue)) + ' ';
      classes += 'sm-p-b-' + getParamValue('smPaddingBottom', getParamValue('paddingBottom', defaultValue)) + ' ';

      return classes;
    },
    [getParamValue]
  );

  const getBackgroundStyle = useCallback(() => {
    let style = {};

    let backgroundUrl = getParamValue('backgroundUrl');
    const domElem = document.getElementById(getMainId());

    if (domElem && backgroundUrl) {
      const elemWidth = domElem.offsetWidth;

      if (elemWidth > 1200) {
        backgroundUrl = getParamValue('backgroundXlUrl', backgroundUrl);
      } else if (elemWidth > 1920) {
        backgroundUrl = getParamValue('backgroundRetinaUrl', backgroundUrl);
      }

      style = {
        ...style,
        backgroundImage: 'url(' + backgroundUrl + ')',
        backgroundSize: getParamValue('backgroundSize', 'cover'),
        backgroundPosition: getParamValue('backgroundPosition', 'left top'),
        backgroundRepeat: getParamValue('backgroundRepeat', 'repeat'),
        backgroundAttachment: getParamValue('backgroundAttachment', 'scroll'),
      };
    }

    const backgroundColor = getParamValue('backgroundColor', 'transparent');
    if (backgroundColor && backgroundColor !== 'transparent') {
      style.backgroundColor = backgroundColor;
    }

    return style;
  }, [getParamValue, getMainId]);

  const getElementStyle = useCallback(() => {
    if (elementStyle) {
      return <style type="text/css" dangerouslySetInnerHTML={{ __html: elementStyle }} />;
    }
    return null;
  }, [elementStyle]);

  const getCssClasses = useCallback(() => {
    return ' ' + getParamValue('cssClasses', '') + ' ';
  }, [getParamValue]);

  // === HTML Append ===
  const getAppendHtml = useCallback(() => {
    const content = getParamValue('appendHtml', '');
    if (content) {
      return <RichEditorContent content={content} />;
    }
    return null;
  }, [getParamValue]);

  const getParallaxData = useCallback(() => {
    const parallaxType = getParamValue('parallaxType', 'none');

    if (effectsEnabled && parallaxType !== 'none') {
      const parallaxProps = {
        disabled: editMode,
      };

      const parallaxInitialOpacity = parseInt(getParamValue('parallaxInitialOpacity', '100'), 10) / 100;
      const parallaxDirection = getParamValue('parallaxDirection', 'center');
      const parallaxIntensity = parseInt(getParamValue('parallaxIntensity', '50'), 10);

      if (parallaxInitialOpacity < 100) {
        parallaxProps.opacity = [parallaxInitialOpacity, 1];
      }

      switch (parallaxType) {
        case 'slide': {
          switch (parallaxDirection) {
            case 'center': {
              const startScale = 1 - parallaxIntensity / 100;
              parallaxProps.scaleX = [startScale, 1];
              parallaxProps.scaleY = [startScale, 1];
              parallaxProps.scaleZ = [startScale, 1];
              break;
            }

            case 'left': {
              const startX = (parallaxIntensity / 100) * layout.width;
              parallaxProps.translateX = [`${startX}px`, '0px'];
              break;
            }

            case 'top': {
              const startY = ((-1 * parallaxIntensity) / 100) * layout.height;
              parallaxProps.translateY = [`${startY}px`, '0px'];
              break;
            }

            case 'bottom': {
              const startY = (parallaxIntensity / 100) * layout.height;
              parallaxProps.translateY = [`${startY}px`, '0px'];
              break;
            }

            default: { // translate right
              const startX = ((-1 * parallaxIntensity) / 100) * layout.width;
              parallaxProps.translateX = [`${startX}px`, '0px'];
              break;
            }
          }
          break;
        }

        case 'zoom': {
          const startScale = 1 - parallaxIntensity / 100;
          parallaxProps.scaleX = [startScale, 1];
          parallaxProps.scaleY = [startScale, 1];
          parallaxProps.scaleZ = [startScale, 1];

          switch (parallaxDirection) {
            case 'right': {
              const startX = ((-1 * parallaxIntensity) / 100) * layout.width;
              parallaxProps.translateX = [`${startX}px`, '0px'];
              break;
            }

            case 'left': {
              const startX = (parallaxIntensity / 100) * layout.width;
              parallaxProps.translateX = [`${startX}px`, '0px'];
              break;
            }

            case 'top': {
              const startY = ((-1 * parallaxIntensity) / 100) * layout.height;
              parallaxProps.translateY = [`${startY}px`, '0px'];
              break;
            }

            case 'bottom': {
              const startY = (parallaxIntensity / 100) * layout.height;
              parallaxProps.translateY = [`${startY}px`, '0px'];
              break;
            }
          }
          break;
        }
      }

      return parallaxProps;
    }

    return null;
  }, [getParamValue, effectsEnabled, layout, editMode]);

  // === Animation ===
  const getAnimationStyle = useCallback(() => {
    let style = '';
    const animationType = getParamValue('animationType', 'none');

    if (effectsEnabled && animationType !== 'none') {
      const stylePrefix = '#' + getMainId();
      const animationName = 'anim_' + index.replace(/\./g, '_');
      const animationInitialOpacity = parseInt(getParamValue('animationInitialOpacity', '0'), 10) / 100;

      switch (animationType) {
        case 'fade': {
          style += '@-webkit-keyframes ' + animationName + ' {\n';
          style += '0% { opacity: ' + animationInitialOpacity + '; } \n';
          style += '100% { opacity: 1; } \n';
          style += '}\n';

          style += '@keyframes ' + animationName + ' {\n';
          style += '0% { opacity: ' + animationInitialOpacity + '; } \n';
          style += '100% { opacity: 1; } \n';
          style += '}\n';
          break;
        }

        case 'slide': {
          const animationDirection = getParamValue('animationDirection', 'center');
          const animationIntensity = parseInt(getParamValue('animationIntensity', '50'), 10);

          let transformType = 'translate3d';
          let transformParams = '0,0,0';
          let transformEndParams = '0,0,0';

          switch (animationDirection) {
            case 'center':
              transformType = 'scale3d';
              transformParams = 1 - animationIntensity / 100;
              transformParams += ',' + transformParams + ',' + transformParams;
              transformEndParams = '1,1,1';
              break;

            case 'right':
              transformParams = '-' + animationIntensity * 2 + '%, 0, 0';
              break;

            case 'left':
              transformParams = animationIntensity * 2 + '%, 0, 0';
              break;

            case 'top':
              transformParams = '0, ' + animationIntensity * 2 + '%, 0';
              break;

            case 'bottom':
              transformParams = '0, -' + animationIntensity * 2 + '%, 0';
              break;
          }

          style += '@-webkit-keyframes ' + animationName + ' {\n';
          style +=
            '0% { opacity: ' +
            animationInitialOpacity +
            '; transform: ' +
            transformType +
            '(' +
            transformParams +
            '); } \n';
          style += '100% { opacity: 1; transform: ' + transformType + '(' + transformEndParams + '); } \n';
          style += '}\n';

          style += '@keyframes ' + animationName + ' {\n';
          style +=
            '0% { opacity: ' +
            animationInitialOpacity +
            '; transform: ' +
            transformType +
            '(' +
            transformParams +
            '); } \n';
          style += '100% { opacity: 1; transform: ' + transformType + '(' + transformEndParams + '); } \n';
          style += '}\n';
          break;
        }

        case 'bounce': {
          const animationDirection = getParamValue('animationDirection', 'center');
          const animationIntensity = parseInt(getParamValue('animationIntensity', '50'), 10);

          let transformType = 'translate';
          let transformMultiplier = 1;
          const calculateTransformValue = (type, multiplier, progress) => {
            if (type === 'scale') {
              return progress * (1 - (1 - progress) * (animationIntensity / 100));
            }
            return (1 - progress) * multiplier * 2 * animationIntensity + '%';
          };

          switch (animationDirection) {
            case 'center':
              transformType = 'scale';
              break;
            case 'right':
              transformType += 'X';
              transformMultiplier = -1;
              break;
            case 'left':
              transformType += 'X';
              break;
            case 'top':
              transformType += 'Y';
              break;
            case 'bottom':
              transformType += 'Y';
              transformMultiplier = -1;
              break;
          }

          let frames = '';
          frames +=
            '0% { opacity: ' +
            animationInitialOpacity +
            '; transform: ' +
            transformType +
            '(' +
            calculateTransformValue(transformType, transformMultiplier, 0.3) +
            '); }\n';
          frames +=
            '50% { opacity: .9;  transform: ' +
            transformType +
            '(' +
            calculateTransformValue(transformType, transformMultiplier, 1.1) +
            '); }\n';
          frames +=
            '80% { opacity: 1; transform: ' +
            transformType +
            '(' +
            calculateTransformValue(transformType, transformMultiplier, 0.89) +
            '); }\n';
          frames +=
            '100% { opacity: 1; transform: ' +
            transformType +
            '(' +
            calculateTransformValue(transformType, transformMultiplier, 1) +
            '); }\n';

          style += '@-webkit-keyframes ' + animationName + ' {\n' + frames + '}\n';
          style += '@keyframes ' + animationName + ' {\n' + frames + '}\n';
          break;
        }

        case 'zoom': {
          const animationDirection = getParamValue('animationDirection', 'center');
          const animationIntensity = parseInt(getParamValue('animationIntensity', '50'), 10);

          let scaleTransformParams = 1 - animationIntensity / 100;
          scaleTransformParams += ',' + scaleTransformParams + ',' + scaleTransformParams;

          let initialTranslate = '';
          let endTranslate = '';
          switch (animationDirection) {
            case 'right':
              initialTranslate = 'translate3d(-' + animationIntensity / 2 + '%, 0, 0)';
              endTranslate = 'translate3d(0,0,0)';
              break;
            case 'left':
              initialTranslate = 'translate3d(' + animationIntensity / 2 + '%, 0, 0)';
              endTranslate = 'translate3d(0,0,0)';
              break;
            case 'top':
              initialTranslate = 'translate3d(0, ' + animationIntensity / 2 + '%, 0)';
              endTranslate = 'translate3d(0,0,0)';
              break;
            case 'bottom':
              initialTranslate = 'translate3d(0, -' + animationIntensity / 2 + '%, 0)';
              endTranslate = 'translate3d(0,0,0)';
              break;
          }

          style += '@-webkit-keyframes ' + animationName + ' {\n';
          style +=
            '0% { opacity: ' +
            animationInitialOpacity +
            '; transform: scale3d(' +
            scaleTransformParams +
            ') ' +
            initialTranslate +
            '; } \n';
          style += '100% { opacity: 1; transform: scale3d(1, 1, 1) ' + endTranslate + '; } \n';
          style += '}\n';

          style += '@keyframes ' + animationName + ' {\n';
          style +=
            '0% { opacity: ' +
            animationInitialOpacity +
            '; transform: scale3d(' +
            scaleTransformParams +
            ') ' +
            initialTranslate +
            '; } \n';
          style += '100% { opacity: 1; transform: scale3d(1, 1, 1) ' + endTranslate + '; } \n';
          style += '}\n';
          break;
        }

        case 'flip': {
          const animationDirection = getParamValue('animationDirection', 'center');
          const animationIntensity = parseInt(getParamValue('animationIntensity', '50'), 10);

          let rotationAxis = 'X';
          let rotationAngle = 90 * (animationIntensity / 100);
          switch (animationDirection) {
            case 'right':
              rotationAxis = 'Y';
              rotationAngle *= -1;
              break;
            case 'left':
              rotationAxis = 'Y';
              break;
            case 'center':
            case 'top':
              rotationAxis = 'X';
              rotationAngle *= -1;
              break;
            case 'bottom':
              rotationAxis = 'X';
              break;
          }

          style += '@-webkit-keyframes ' + animationName + ' {\n';
          style +=
            '0% { opacity: ' +
            animationInitialOpacity +
            '; transform: perspective(2000px) rotate' +
            rotationAxis +
            '(' +
            rotationAngle +
            'deg); } \n';
          style += '100% { opacity: 1; transform: rotate' + rotationAxis + '(0); } \n';
          style += '}\n';

          style += '@keyframes ' + animationName + ' {\n';
          style +=
            '0% { opacity: ' +
            animationInitialOpacity +
            '; transform: perspective(2000px) rotate' +
            rotationAxis +
            '(' +
            rotationAngle +
            'deg); } \n';
          style += '100% { opacity: 1; transform: rotate' + rotationAxis + '(0); } \n';
          style += '}\n';
          break;
        }

        case 'fold': {
          const animationDirection = getParamValue('animationDirection', 'center');
          const animationIntensity = parseInt(getParamValue('animationIntensity', '50'), 10);

          let rotationAxis = 'X';
          let rotationAngle = 90 * (animationIntensity / 100);
          let transformOrigin = 'bottom';
          switch (animationDirection) {
            case 'right':
              rotationAxis = 'Y';
              rotationAngle *= -1;
              transformOrigin = 'left';
              break;
            case 'left':
              rotationAxis = 'Y';
              transformOrigin = 'right';
              break;
            case 'center':
            case 'top':
              rotationAxis = 'X';
              rotationAngle *= -1;
              transformOrigin = 'bottom';
              break;
            case 'bottom':
              rotationAxis = 'X';
              transformOrigin = 'top';
              break;
          }

          style += '\n' + stylePrefix + ' {';
          style +=
            '-webkit-transform-origin:' +
            transformOrigin +
            '; -ms-transform-origin:' +
            transformOrigin +
            '; transform-origin:' +
            transformOrigin +
            ';';
          style += '}\n';

          style += '@-webkit-keyframes ' + animationName + ' {\n';
          style +=
            '0% { opacity: ' +
            animationInitialOpacity +
            '; transform: perspective(2000px) rotate' +
            rotationAxis +
            '(' +
            rotationAngle +
            'deg); } \n';
          style += '100% { opacity: 1; transform: rotate' + rotationAxis + '(0); } \n';
          style += '}\n';

          style += '@keyframes ' + animationName + ' {\n';
          style +=
            '0% { opacity: ' +
            animationInitialOpacity +
            '; transform: perspective(2000px) rotate' +
            rotationAxis +
            '(' +
            rotationAngle +
            'deg); } \n';
          style += '100% { opacity: 1; transform: rotate' + rotationAxis + '(0); } \n';
          style += '}\n';
          break;
        }

        case 'roll': {
          const animationDirection = getParamValue('animationDirection', 'center');
          const animationIntensity = parseInt(getParamValue('animationIntensity', '50'), 10);

          let rotationAngle = 360 * (animationIntensity / 100);
          let transformOrigin = 'bottom';
          switch (animationDirection) {
            case 'right':
              rotationAngle *= -1;
              transformOrigin = 'left';
              break;
            case 'left':
              transformOrigin = 'right';
              break;
            case 'center':
            case 'top':
              rotationAngle *= -1;
              transformOrigin = 'bottom';
              break;
            case 'bottom':
              transformOrigin = 'top';
              break;
          }

          style += '\n' + stylePrefix + ' {';
          style +=
            '-webkit-transform-origin:' +
            transformOrigin +
            '; -ms-transform-origin:' +
            transformOrigin +
            '; transform-origin:' +
            transformOrigin +
            ';';
          style += '}\n';

          style += '@-webkit-keyframes ' + animationName + ' {\n';
          style += '0% { opacity: ' + animationInitialOpacity + '; transform: rotateZ(' + rotationAngle + 'deg); } \n';
          style += '100% { opacity: 1; transform: rotateZ(0); } \n';
          style += '}\n';

          style += '@keyframes ' + animationName + ' {\n';
          style += '0% { opacity: ' + animationInitialOpacity + '; transform: rotateZ(' + rotationAngle + 'deg); } \n';
          style += '100% { opacity: 1; transform: rotateZ(0); } \n';
          style += '}\n';
          break;
        }
      }

      style += '\n' + stylePrefix + '.elem-shown { ';
      style += 'animation-name: ' + animationName + '; ';
      style += 'animation-fill-mode: both; ';
      style += 'animation-duration: ' + getParamValue('animationDuration', '1000') + 'ms; ';
      style += '-webkitanimation-name: ' + animationName + '; ';
      style += '-webkitanimation-duration: ' + getParamValue('animationDuration', '1000') + 'ms; ';
      style += 'animation-delay: ' + getParamValue('animationDelay', '0') + 'ms; ';
      style += 'animation-timing-function: ' + getParamValue('animationSpeedCurve', 'ease-in-out') + '; ';
      style += ' }';
    }

    return style;
  }, [getParamValue, getMainId, index, effectsEnabled]);

  // === Style Processing ===
  const processStyleRulesets = useCallback(
    (rule) => {
      let style = '';
      let selector = rule.selector;

      if (selector.indexOf('self') === 0) {
        selector = selector.replace('self', '');
      }

      if (selector !== ':after' && selector !== ':before') {
        selector = ' ' + selector;
      }

      style += '#' + getMainId() + selector + ' { ';

      rule.walkDecls((decl) => {
        style += decl.prop + ': ' + decl.value + '; ';
      });

      style += ' } ';

      return style;
    },
    [getMainId]
  );

  const processStyle = useCallback(async () => {
    const customStyle = getParamValue('style');
    const animationStyle = getAnimationStyle();

    if (customStyle) {
      try {
        const ast = postcss.parse(customStyle);
        let style = '';

        ast.walkRules((rule) => {
          style += processStyleRulesets(rule);
        });

        ast.walkAtRules('media', (media) => {
          style += '@media ';
          style += media.params + ' { ' + processStyleRulesets(media) + ' } ';
        });

        style += '\n' + animationStyle;

        setElementStyle(style);
      } catch (err) {
        return null;
      }
    } else if (animationStyle) {
      setElementStyle(animationStyle);
    }
  }, [getParamValue, getAnimationStyle, processStyleRulesets]);

  // === Lifecycle (replaces componentDidMount, componentDidUpdate, componentWillUnmount) ===
  useEffect(() => {
    // ComponentDidMount Equivalent
    if (!isMounted.current) {
      processStyle();
      isMounted.current = true;
      return;
    }

    // ComponentDidUpdate Equivalent
    const getPrevParamValue = (name, defaultValue = null) => {
      const param = _find(prevProps.current.params, ['name', name]);
      if (param && param.value !== null && param.value !== '') {
        return param.value;
      }
      return defaultValue;
    };

    const currentStyle = getParamValue('style');
    const prevStyle = getPrevParamValue('style');

    const animationType = getParamValue('animationType', 'none');
    const prevAnimationType = getPrevParamValue('animationType', 'none');

    if (animationType !== prevAnimationType) {
      setShown(false);
      processStyle();

      if (animationResetTimeout.current) {
        clearTimeout(animationResetTimeout.current);
      }
      animationResetTimeout.current = setTimeout(() => setShown(true), 200);
    } else if ((currentStyle || null) !== (prevStyle || null)) {
      if (currentStyle) {
        processStyle();
      } else {
        setElementStyle(null);
      }
    }

    // Update prevProps for the next render cycle
    prevProps.current = props;
  }, [props.params, props.index, props.effectsEnabled, processStyle, getParamValue]);

  // ComponentWillUnmount Equivalent
  useEffect(() => {
    return () => {
      allowStateChange.current = false;
      if (animationResetTimeout.current) {
        clearTimeout(animationResetTimeout.current);
      }
    };
  }, []);

  // Return all methods and states so that the new function component can access them
  return {
    shown,
    setShown,
    triggerShown,
    getMainId,
    getCustomId,
    getParam,
    getParamValue,
    triggerShowSettings,
    getResponsiveClasses,
    getPaddingClasses,
    getBackgroundStyle,
    getElementStyle,
    getParallaxData,
    getAnimationStyle,
    getAppendHtml,
    getCssClasses,
  };
};
